import { UnknownSpec } from '@/core';
import { DataQueryPlugin, QueryPluginContext, QueryPluginGetDataProgressiveResponse } from '../../model';
import { DataQuery } from './DataQuery';

/**
 * More opinionated implementation of class-based data query plugins that supports progressive
 * loading of content.
 *
 * MUST be used when implementing a data query plugin with progressive loading.
 */
export abstract class ProgressiveDataQuery<Spec = UnknownSpec> extends DataQuery<Spec> {
  // Note that the logic in this class assumes that a ProgressiveDataQuery instance is used for a single
  // `queryFn` call from react-query and is not re-used. If that ever changes, the logic around cancellation
  // and abort controllers will likely need to be adjusted.

  /**
   * Internal abort controller, which should be used to manage cancellation of in flight events,
   * fetching, etc. when the data query is cancelled using `cancel`.
   * https://developer.mozilla.org/en-US/docs/Web/API/AbortController
   *
   * We cannot rely on the signal from react-query because react-query does not natively support
   * progressive loading and assumes the abort controller is no longer needed after the first
   * result comes back. It also does not give us direct access to the abort controller, which
   * we need for the `cancel` method.
   */
  protected abortController: AbortController;

  constructor() {
    super();

    this.abortController = new AbortController();
  }

  /**
   * This fetch function handles the abort signal for plugins, allowing the framework to manage query cancellation
   */
  public async fetch(url: string, options?: RequestInit): Promise<Response> {
    // Progressive query plugins manage their cancellation via their own abort controller because they cannot
    // rely on react-query to manage cancellation of additional queries.
    return await fetch(url, { ...options, signal: this.abortController.signal });
  }

  /**
   * Function that manages cancellation of progressive query plugins. This is automatically called
   * by the framework based on relevant events from react-query. The plugin may also call it internally
   * if there are additional cases where cancellation is necessary.
   *
   * By default, this function handles:
   * - Aborting the abort controller.
   *
   * Implementations of progressive data query plugins may implement this function to handle any
   * additional cleanup not handled here. However, they should ensure they call `super.cancel()` to
   * get the functionality described above.
   */
  public cancel() {
    // Abort abort controller.
    this.abortController.abort();
  }

  abstract getData(spec: Spec, ctx: QueryPluginContext): Promise<QueryPluginGetDataProgressiveResponse>;

  public getComparableData?(spec: Spec, ctx: QueryPluginContext): Promise<QueryPluginGetDataProgressiveResponse>;
}

/**
 * Returns true if the plugin instance is a progressive data query plugin.
 */
export function isProgressiveDataQueryPlugin(
  pluginInstance: DataQueryPlugin<UnknownSpec> | DataQuery<UnknownSpec> | ProgressiveDataQuery<UnknownSpec>
): pluginInstance is ProgressiveDataQuery {
  return pluginInstance instanceof ProgressiveDataQuery;
}
